/**
 * THE DECK RUNTIME, v1 — the framework-owned half of artifact motion and structure.
 *
 * The open-slide study's verdict was that the wow is prose discipline plus FRAMEWORK-owned choreography.
 * Artifacts took the prose discipline (the contract) and left the framework role empty, which is why a
 * request like "reveal the title word by word" used to collide with the contract's motion rules and lose.
 * This module is that framework role, taken by Deck itself: authors declare intent as data, the runtime
 * owns the mechanics, and because it is INJECTED AT RENDER TIME rather than carried by the document,
 * every artifact ever made gets better when this file does.
 *
 * WHAT IT OWNS, all declared in markup, none of it reimplementable in document script:
 *   data-reveal="words"   words enter one by one; cadence compressed so the whole reveal obeys the
 *                         contract's settle law regardless of word count
 *   data-count-to         the element's text IS the final value; it counts up and ends at exactly
 *                         that text
 *   data-stagger          a container's children enter in sequence with no per-child delay rules
 *   <deck-stat> <deck-quote> <deck-list>
 *                         platform components: light-DOM, token-consuming, sealed
 *
 * THE FOUR DISCIPLINES, each load-bearing:
 *
 *   LIGHT DOM ONLY. Components populate their own children, never a shadow root. The bake serializer
 *   clones the live DOM and pairs elements positionally; shadow trees would be invisible to it and the
 *   rest tier would show empty hosts. Light DOM means the bake sees plain markup and docBaked stays
 *   self-contained even though docHtml now leans on the platform.
 *
 *   WAAPI, NOT STYLESHEETS. Behaviors animate via element.animate(), which document.getAnimations()
 *   reports, so the bridge's settleAnimations() finishes them at bake exactly like authored CSS
 *   entrances, and the freeze bridge ignores them (finite, never infinite). Zero new bake states.
 *
 *   RESTORE AFTER SETTLE. data-reveal splits text into spans and data-count-to rewrites text per frame,
 *   and both put the ORIGINAL text back the moment they finish. The settled DOM equals the authored DOM,
 *   so the bake captures clean markup and the editor's path contract (element-child index chains
 *   replayed against source) never meets a node the source does not have. Components are the one
 *   deliberate exception: their expansion persists, and docBridge seals them (picks map to the host,
 *   text edit refuses) so no path into expanded internals can ever be committed.
 *
 *   IDEMPOTENT AND ARRIVAL-DRIVEN. Every processed element is marked data-dr-done; a MutationObserver
 *   processes late arrivals. Deliberately NOT injected into the docStream shell: the shell syncs
 *   parser output into its stage positionally, and runtime mutations inside that tree would fight the
 *   sync. Behaviors play on the settled remount after a stream ends, which is the same moment the
 *   card replays entrances today.
 *
 * The snippet embeds the pure helpers below via Function.prototype.toString, the same single-source
 * trick tsxShell uses, so the tested functions and the shipped ones are the same text. That constrains
 * their style: plain statements, no template literals, no TS syntax inside bodies.
 */

/** Written into every manifest as "runtime". Bumps only when shipped behavior changes meaningfully. */
export const DECK_RUNTIME_VERSION = 1

/** Marker attribute shared with docBridge (BRIDGE_MARK, pinned there): the bake serializer strips nodes
 *  carrying it, so the runtime script never survives into baked output. */
const RUNTIME_MARK = 'data-bds-x'

/**
 * Text split into word and whitespace runs, both preserved verbatim, so joining the tokens reproduces
 * the input exactly. Words become spans, whitespace stays text nodes (an inline-block span would swallow
 * a trailing space and break line wrapping).
 */
export function splitRevealTokens(text: string): string[] {
  var out: string[] = []
  var parts = String(text).split(/(\s+)/)
  for (var i = 0; i < parts.length; i++) {
    if (parts[i] !== '') out.push(parts[i])
  }
  return out
}

export interface CountParts {
  prefix: string
  value: number
  decimals: number
  suffix: string
  grouped: boolean
}

/**
 * Reads "the final value as text" into something a count-up can interpolate: "$1,240" and "48.4M" and
 * "97%" all work, and anything without a leading-ish number returns null so the behavior declines
 * rather than mangling copy.
 */
export function parseCountText(text: string): CountParts | null {
  var m = /^([^0-9-]*)(-?[0-9][0-9,]*(?:\.[0-9]+)?)([\s\S]*)$/.exec(String(text))
  if (!m) return null
  var numeric = m[2]
  var value = parseFloat(numeric.replace(/,/g, ''))
  if (!isFinite(value)) return null
  var dot = numeric.indexOf('.')
  return {
    prefix: m[1],
    value: value,
    decimals: dot >= 0 ? numeric.length - dot - 1 : 0,
    suffix: m[3],
    grouped: numeric.indexOf(',') >= 0,
  }
}

/** The text to show at progress p of the count, matching the target's own formatting. */
export function formatCountText(parts: CountParts, p: number): string {
  var v = parts.value * p
  var fixed = Math.abs(v).toFixed(parts.decimals)
  if (parts.grouped) {
    var pieces = fixed.split('.')
    pieces[0] = pieces[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    fixed = pieces.join('.')
  }
  return parts.prefix + (v < 0 ? '-' : '') + fixed + parts.suffix
}

/**
 * The injected script. Composed once at module load; docBridge prepends it to its own snippet inside
 * withDocBridge, so every surface that already gets the bridge (live cards, the bake warmer, the
 * artifact editor, present mode) gets the runtime with zero new call sites.
 */
export const DECK_RUNTIME_SNIPPET: string =
  `<script ${RUNTIME_MARK}>(function(){
'use strict';
if(window.__deckR)return;window.__deckR=${DECK_RUNTIME_VERSION};
var reduced=false;
try{reduced=window.matchMedia&&window.matchMedia('(prefers-reduced-motion: reduce)').matches}catch(e){}
var splitRevealTokens=${splitRevealTokens.toString()};
var parseCountText=${parseCountText.toString()};
var formatCountText=${formatCountText.toString()};
var EASE='cubic-bezier(0,0,0.2,1)';

function revealWords(el){
  if(el.children.length>0)return;
  var original=el.textContent||'';
  var toks=splitRevealTokens(original);
  var words=0;
  for(var i=0;i<toks.length;i++)if(!/^\\s+$/.test(toks[i]))words++;
  if(words<2)return;
  el.style.animation='none';
  el.style.opacity='1';
  if(reduced)return;
  var cad=Math.min(90,Math.max(30,Math.floor(600/words)));
  var frag=document.createDocumentFragment();
  var idx=0;
  for(var j=0;j<toks.length;j++){
    if(/^\\s+$/.test(toks[j])){frag.appendChild(document.createTextNode(toks[j]));continue}
    var sp=document.createElement('span');
    sp.textContent=toks[j];
    sp.style.display='inline-block';
    try{
      var a=sp.animate([{opacity:0,transform:'translateY(0.35em)'},{opacity:1,transform:'none'}],{duration:500,delay:idx*cad,easing:EASE,fill:'both'});
      if(a&&a.pause){}
    }catch(e){}
    frag.appendChild(sp);
    idx++;
  }
  el.textContent='';
  el.appendChild(frag);
  setTimeout(function(){try{el.textContent=original}catch(e){}},500+cad*Math.max(0,idx-1)+80);
}

function countUp(el){
  if(el.children.length>0)return;
  var original=(el.textContent||'').trim();
  var parts=parseCountText(original);
  if(!parts||reduced)return;
  var t0=null;
  function tick(ts){
    if(t0===null)t0=ts;
    var p=Math.min(1,(ts-t0)/900);
    var e2=1-Math.pow(1-p,3);
    try{el.textContent=p>=1?original:formatCountText(parts,e2)}catch(err){}
    if(p<1)requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

function staggerChildren(el){
  var kids=el.children;
  if(kids.length<2||reduced)return;
  var cad=Math.min(80,Math.max(30,Math.floor(600/kids.length)));
  var n=0;
  for(var i=0;i<kids.length;i++){
    var k=kids[i];
    if(k.hasAttribute('data-step'))continue;
    try{k.animate([{opacity:0,transform:'translateY(14px)'},{opacity:1,transform:'none'}],{duration:500,delay:n*cad,easing:EASE,fill:'both'})}catch(e){}
    n++;
  }
}

function mk(tag,style,text){
  var d=document.createElement(tag);
  if(style)d.setAttribute('style',style);
  if(text!=null&&text!=='')d.textContent=text;
  return d;
}

function compStat(host){
  var value=host.getAttribute('value')||'';
  var label=host.getAttribute('label')||'';
  var delta=host.getAttribute('delta')||'';
  while(host.firstChild)host.removeChild(host.firstChild);
  host.style.display='block';
  var row=mk('div','display:flex;align-items:baseline;gap:16px',null);
  row.appendChild(mk('div','font-family:var(--a-font-display);font-size:92px;line-height:1.05;letter-spacing:-0.02em;color:var(--a-text)',value));
  if(delta)row.appendChild(mk('div','font-family:var(--a-font-body);font-size:28px;line-height:1.2;color:var(--a-accent)',delta));
  host.appendChild(row);
  if(label)host.appendChild(mk('div','font-family:var(--a-font-body);font-size:24px;line-height:1.4;color:var(--a-muted);margin-top:10px',label));
}

function compQuote(host){
  var cite=host.getAttribute('cite')||'';
  var text=(host.textContent||'').trim();
  while(host.firstChild)host.removeChild(host.firstChild);
  host.style.display='block';
  host.appendChild(mk('div','font-family:var(--a-font-display);font-size:54px;line-height:1.25;letter-spacing:-0.01em;color:var(--a-text)',text));
  if(cite)host.appendChild(mk('div','font-family:var(--a-font-body);font-size:22px;line-height:1.4;color:var(--a-muted);margin-top:18px',cite));
}

var ICON_CHECK='<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex:none;transform:translateY(2px)"><path d="M4 12.5l5 5L20 6.5"></path></svg>';
var ICON_CROSS='<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="flex:none;transform:translateY(2px)"><path d="M6 6l12 12M18 6L6 18"></path></svg>';

function compList(host){
  var kind=host.getAttribute('kind')==='cross'?'cross':'check';
  host.style.display='block';
  host.style.listStyle='none';
  host.style.margin='0';
  host.style.padding='0';
  var items=host.querySelectorAll('li');
  for(var i=0;i<items.length;i++){
    var li=items[i];
    if(li.getAttribute('data-dr-li'))continue;
    li.setAttribute('data-dr-li','1');
    var k=li.getAttribute('kind')||kind;
    li.style.listStyle='none';
    li.style.display='flex';
    li.style.alignItems='baseline';
    li.style.gap='14px';
    if(!li.style.margin)li.style.margin='0 0 14px 0';
    if(!li.style.fontSize)li.style.fontSize='32px';
    if(!li.style.lineHeight)li.style.lineHeight='1.5';
    if(!li.style.fontFamily)li.style.fontFamily='var(--a-font-body)';
    if(!li.style.color)li.style.color='var(--a-text)';
    var ic=document.createElement('span');
    ic.style.color=k==='cross'?'var(--a-muted)':'var(--a-accent)';
    ic.innerHTML=k==='cross'?ICON_CROSS:ICON_CHECK;
    li.insertBefore(ic,li.firstChild);
  }
  if(host.hasAttribute('data-stagger'))staggerChildren(host);
}

var SEL='[data-reveal],[data-count-to],[data-stagger],deck-stat,deck-quote,deck-list';

function handle(el){
  if(el.getAttribute('data-dr-done'))return;
  el.setAttribute('data-dr-done','1');
  var tag=String(el.tagName||'').toLowerCase();
  if(tag==='deck-stat'){compStat(el);return}
  if(tag==='deck-quote'){compQuote(el);return}
  if(tag==='deck-list'){compList(el);return}
  if(el.hasAttribute('data-count-to')){countUp(el);return}
  if(el.getAttribute('data-reveal')==='words'){revealWords(el);return}
  if(el.hasAttribute('data-stagger'))staggerChildren(el);
}

function process(root){
  if(!root||root.nodeType!==1)return;
  try{if(root.matches&&root.matches(SEL))handle(root)}catch(e){}
  var list;
  try{list=root.querySelectorAll(SEL)}catch(e){return}
  for(var i=0;i<list.length;i++)handle(list[i]);
}

process(document.documentElement);
try{
  new MutationObserver(function(ms){
    for(var i=0;i<ms.length;i++){
      var added=ms[i].addedNodes;
      for(var j=0;j<added.length;j++)process(added[j]);
    }
  }).observe(document.documentElement,{childList:true,subtree:true});
}catch(e){}
})()<` +
  `/script>`
